import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { session_token, user } = response.data;
      
      localStorage.setItem('session_token', session_token);
      
      // Verify admin access
      const adminCheck = await axios.get(`${API}/admin/verify`, {
        headers: { 'Authorization': `Bearer ${session_token}` }
      });
      
      if (adminCheck.data.is_admin) {
        toast.success('Welcome, Admin!');
        navigate('/admin');
      } else {
        toast.error('This account does not have admin access');
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `${BACKEND_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }

    try {
      await axios.post(`${API}/auth/request-reset`, { email: resetEmail });
      toast.success('Password reset instructions sent! Check the console for your reset token.');
      setShowForgotPassword(false);
    } catch (error) {
      toast.error('Failed to send reset email');
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--navy-50)' }}>
        <div className="max-w-md w-full bg-white p-8 rounded-lg border border-gray-200 shadow-sm">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Reset Password
            </h1>
            <p className="text-gray-600">Enter your email to receive reset instructions</p>
          </div>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Email</label>
              <Input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="admin@bragbox.com"
              />
            </div>

            <Button
              type="submit"
              className="w-full py-6 rounded-lg font-medium"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Send Reset Link
            </Button>

            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="text-sm text-gray-600 hover:text-navy-900 w-full text-center"
            >
              ← Back to login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--navy-50)' }}>
      <div className="max-w-md w-full bg-white p-8 rounded-lg border border-gray-200 shadow-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--navy-900)' }}>
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
            Admin Login
          </h1>
          <p className="text-gray-600">Sign in to access the admin dashboard</p>
        </div>

        <div className="space-y-4">
          <Button
            onClick={handleGoogleLogin}
            className="w-full py-6 text-base font-medium rounded-lg flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
          >
            <LogIn className="w-5 h-5" />
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or</span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@bragbox.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Password</label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 rounded-lg font-medium"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <button
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-center w-full"
            style={{ color: 'var(--navy-600)' }}
          >
            Forgot password?
          </button>

          <div className="text-center pt-4 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-navy-900"
            >
              ← Back to home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;